import React, { useState, useEffect } from "react";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { Bell, Building2, ChevronDown, LogOut, User, Menu } from "lucide-react";
import { TID } from "@/constants/testIds";
import { GlobalSearch } from "@/components/common/AssistantAndSearch";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useNavigate } from "react-router-dom";

export default function Topbar({ onOpenMobileNav }) {
  const { user, organizations, activeOrgId, switchOrg, logout } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    if (!activeOrgId) return;
    api.get("/companies").then(({ data }) => {
      if (!cancelled) {
        setCompanies(data.items || []);
        setSelectedCompany(null);
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [activeOrgId]);

  const activeOrg = organizations.find((o) => o.id === activeOrgId);
  const initials = `${(user?.first_name || "?")[0]}${(user?.last_name || "")[0] || ""}`.toUpperCase();
  const isBeta = !!(user?.beta_program?.enrolled);
  // On masque totalement le sélecteur d'organisation quand il n'y en a qu'une seule.
  // La gestion des organisations reste accessible depuis le menu latéral / Paramètres.
  const showOrgSwitcher = (organizations?.length || 0) > 1;

  return (
    <header className="sticky top-0 z-30 flex h-16 items-center gap-3 border-b border-border bg-background/85 backdrop-blur-md px-4 sm:px-6 lg:pl-72 lg:pr-10">
      <button className="lg:hidden text-foreground" onClick={onOpenMobileNav} aria-label="Menu">
        <Menu className="h-5 w-5" />
      </button>

      {/* Org switcher — visible uniquement si l'utilisateur a plusieurs organisations */}
      {showOrgSwitcher && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              data-testid={TID.topbarOrgSwitcher}
              className="flex items-center gap-2 rounded-xl border border-border bg-card px-3 py-1.5 text-sm font-medium hover:bg-secondary transition-colors">
              <div className="flex h-6 w-6 items-center justify-center rounded-md bg-primary text-primary-foreground text-[10px] font-bold">
                {activeOrg?.name?.[0]?.toUpperCase() || "P"}
              </div>
              <span className="max-w-[180px] truncate">{activeOrg?.name || "Organisation"}</span>
              <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-64">
            <DropdownMenuLabel>Vos organisations</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {organizations.map((o) => (
              <DropdownMenuItem key={o.id} onClick={() => { switchOrg(o.id); window.location.reload(); }}>
                <span className="truncate">{o.name}</span>
                {o.is_demo ? <span className="ml-auto rounded-full bg-accent/15 px-2 py-0.5 text-[10px] font-semibold uppercase text-accent">Démo</span> : null}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      {/* Company/SCI switcher — masqué quand aucune société / SCI n'existe */}
      {companies.length > 0 && (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            data-testid={TID.topbarCompanySwitcher}
            className="hidden sm:flex items-center gap-2 rounded-xl border border-border bg-card px-3 py-1.5 text-sm font-medium hover:bg-secondary transition-colors">
            <Building2 className="h-4 w-4 text-muted-foreground" />
            <span className="max-w-[160px] truncate">{selectedCompany?.name || "Toutes les sociétés"}</span>
            <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-64">
          <DropdownMenuLabel>Société / SCI</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => setSelectedCompany(null)}>Toutes les sociétés</DropdownMenuItem>
          {companies.map((c) => (
            <DropdownMenuItem key={c.id} onClick={() => setSelectedCompany(c)}>
              <span className="truncate">{c.name}</span>
              <span className="ml-auto text-[10px] uppercase text-muted-foreground">{c.legal_form}</span>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
      )}

      <div className="ml-auto flex items-center gap-2">
        <GlobalSearch />
        <Button
          variant="ghost" size="icon"
          data-testid={TID.topbarNotifications}
          className="relative rounded-full">
          <Bell className="h-5 w-5" />
        </Button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              data-testid={TID.topbarProfile}
              className="flex items-center gap-2 rounded-full border border-border bg-card px-2 py-1 pr-3 hover:bg-secondary transition-colors">
              <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-bold">
                {initials}
              </div>
              <span className="hidden sm:inline text-sm font-medium">
                {user?.first_name} {user?.last_name}
              </span>
              {isBeta && (
                <span
                  data-testid="beta-badge"
                  title="Membre du programme bêta — remise permanente de 50 % lors du lancement"
                  className="hidden sm:inline-flex items-center rounded-full bg-accent/15 text-accent px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide"
                >
                  Bêta
                </span>
              )}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel className="flex flex-col">
              <span className="font-heading">{user?.first_name} {user?.last_name}</span>
              <span className="text-xs text-muted-foreground">{user?.email}</span>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate("/app/settings")}>
              <User className="mr-2 h-4 w-4" /> Paramètres du compte
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem data-testid={TID.logoutBtn} onClick={async () => { await logout(); navigate("/login"); }}>
              <LogOut className="mr-2 h-4 w-4" /> Se déconnecter
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
